import { useState, useEffect } from "react";
import { useCreateTeam, useUpdateTeam } from "@/hooks/queries/use-teams";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Team } from "@/lib/api/types";
import { getChildTeamLevel } from "@/lib/team-hierarchy";

interface TeamFormProps {
  team?: Team;
  parentTeam?: Pick<Team, "id" | "name" | "level">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamForm({ team, parentTeam, open, onOpenChange }: TeamFormProps) {
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [level, setLevel] = useState<"group" | "product-line" | "product" | "discipline">("group");
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [error, setError] = useState("");

  const isEditing = !!team;

  useEffect(() => {
    if (open) {
      setName(team?.name ?? "");
      setKey(team?.key ?? "");
      setLevel((team?.level ?? getChildTeamLevel(parentTeam?.level) ?? "group") as typeof level);
      setKeyManuallyEdited(!!team);
      setError("");
    }
  }, [team, parentTeam, open]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEditing && !keyManuallyEdited) {
      const autoKey = value
        .replace(/[^a-zA-Z]/g, "")
        .substring(0, 3)
        .toUpperCase();
      setKey(autoKey);
    }
  };

  const handleKeyChange = (value: string) => {
    const sanitized = parentTeam
      ? value.toUpperCase().replace(/[^A-Z0-9-]/g, "")
      : value.toUpperCase().replace(/[^A-Z]/g, "");
    setKey(sanitized);
    setKeyManuallyEdited(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) return;
    if (!isEditing && !key.trim()) {
      setError("Key is required");
      return;
    }

    try {
      if (isEditing) {
        await updateTeam.mutateAsync({ id: team.id, name: name.trim() });
      } else {
        await createTeam.mutateAsync({
          name: name.trim(),
          key: key.trim().toUpperCase(),
          level,
          ...(parentTeam ? { parentId: parentTeam.id } : {}),
        });
      }
      onOpenChange(false);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "data" in err) {
        const data = (err as { data: { message?: string } }).data;
        setError(data?.message || "Failed to save team");
      } else {
        setError("Failed to save team");
      }
    }
  };

  const isPending = createTeam.isPending || updateTeam.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? "Edit Team"
                : parentTeam
                  ? `Create Child Team${parentTeam.name ? ` under ${parentTeam.name}` : ""}`
                  : "Create Team"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {error && <div className="text-sm text-destructive">{error}</div>}
            {parentTeam ? (
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-muted-foreground">
                Parent team: <span className="text-foreground">{parentTeam.name}</span>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="name">Team Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => { handleNameChange(e.target.value); }}
                placeholder="Engineering"
                required
              />
            </div>
            {!isEditing && (
              <div className="grid gap-2">
                <Label htmlFor="key">Team Key</Label>
                <Input
                  id="key"
                  value={key}
                  onChange={(e) => {
                    handleKeyChange(
                      parentTeam ? e.target.value.replace(/[^A-Za-z0-9-]/g, "") : e.target.value.replace(/[^A-Za-z]/g, "")
                    );
                  }}
                  placeholder={parentTeam ? "ENG-PLAT" : "ENG"}
                  pattern={parentTeam ? "^[A-Z][A-Z0-9-]*$" : "^[A-Z]+$"}
                  title={
                    parentTeam
                      ? "Child team keys must start with an uppercase letter and can include uppercase letters, digits, or hyphens"
                      : "Root team keys must contain only uppercase letters (A-Z)"
                  }
                  maxLength={10}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {parentTeam
                    ? "Short identifier using uppercase letters, digits, or hyphens (e.g., ENG-PLAT)"
                    : "Short identifier using uppercase letters only (e.g., ENG, PRD, COM)"}
                </p>
              </div>
            )}
            {!isEditing && (
              <div className="grid gap-2">
                <Label htmlFor="level">Team Level</Label>
                <select
                  id="level"
                  value={level}
                  onChange={(e) => { setLevel(e.target.value as typeof level); }}
                  disabled={!!parentTeam}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  {parentTeam ? (
                    <option value={level}>
                      {level === "product-line"
                        ? "Product Line"
                        : level === "product"
                          ? "Product"
                          : level === "discipline"
                            ? "Discipline"
                            : "Group"}
                    </option>
                  ) : (
                    <>
                      <option value="group">Group</option>
                      <option value="product-line">Product Line</option>
                      <option value="product">Product</option>
                      <option value="discipline">Discipline</option>
                    </>
                  )}
                </select>
                <p className="text-xs text-muted-foreground">
                  {parentTeam
                    ? "Child team level is derived from the selected parent."
                    : "Organizational level for this team"}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onOpenChange(false); }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim() || (!isEditing && !key.trim())}>
              {isPending ? "Saving..." : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
